import {get} from "lodash";

import initConfig from '@/configs';
import {injectable} from "inversify";
import {FormationDefinitionType} from "@/modules/team/types";
import {CountryEnum, PositionAreaEnum, PositionEnum} from "@/modules/base/enums";
import {SponsorTypeEnum} from "@/modules/sponsoring/enums";
import {GroundSlot} from "@/modules/club-grounds/entity";
import {GroundSlotTypeEnum} from "@/modules/club-grounds/enums";
import {Stadium, Terrace} from "@/modules/stadium/entity";
import {FoundationDefinitionType} from "@/modules/stadium/types";
import {TerraceTypeEnum} from "@/modules/stadium/enums";
import {Player, Team} from "@/modules/team/entity";
import {CardEnum} from "@/modules/match/enums";
import {AIQualityLevelEnum} from "@/modules/ai/enums";
import {AIQualityServiceDefinitionType} from "@/modules/ai/types";
import {Negotiator, StaffMember} from "@/modules/staff/entity";
import {Scout} from "@/modules/scouting/entity";
import {Coach} from "@/modules/training/entity";
import {GateTypeEnum} from "@/modules/action-points/enums";
import {Competition} from "@/modules/competitions/entity";
import {FormationEnum} from "@/modules/team/enums";
import {Match} from "@/modules/match/entity";

type SponsorBaseValuesType = {
  perLeagueMatch: number,
  perLeagueVictory: number,
  perCupMatch: number,
  perCupVictory: number,
  once: number,
};

type RangeType = { min: number, max: number };

@injectable()
export class ConfigRepository {
  private items: object = {};

  constructor() {
    this.items = initConfig;
  }

  public get(key: 'debug'): boolean;

  public get(key: 'general.auto_save.minimum_wait_for'): number;
  public get(key: 'general.playableCountries'): CountryEnum[];
  public get(key: 'general.international.countries'): { [key in CountryEnum]?: number };
  public get(key: 'general.international.teamsPerCountry'): number;
  public get(key: 'general.startBalance'): number;
  public get(key: 'general.seasonStartYear'): number;

  public get(key: 'coins.enabled'): boolean;
  public get(key: 'coins.startAmount'): number;

  public get(key: 'ai.services'): { [key in AIQualityLevelEnum]: AIQualityServiceDefinitionType };
  public get(key: 'transferMarket.directTransfer.priceRange'): RangeType;
  public get(key: 'transferMarket.random.aiOfferPriceRange'): RangeType;
  public get(key: 'transferMarket.random.transfersPerWeek'): (teamCount: number) => number;

  public get(key: 'transferMarket.random.percentChanceForOffer'): number;
  public get(key: 'transferMarket.random.aiOfferPriceRange'): RangeType;

  public get(key: 'transferMarket.listings.percentChanceForOffer'): number;
  public get(key: 'transferMarket.listings.offerLimitPerListing'): number;
  public get(key: 'transferMarket.listings.aiOfferPriceRange'): RangeType;
  public get(key: 'transferMarket.listings.targetCount'): (listingCount: number, teamCount: number) => number;
  public get(key: 'transferMarket.listings.removeCount'): (listingCount: number, teamCount: number) => number;

  public get(key: 'stadium.startCapacity'): number;
  public get(key: 'stadium.entranceFee.min'): number;
  public get(key: 'stadium.entranceFee.max'): number;
  public get(key: 'stadium.foundation.maxLevel'): number;
  public get(key: 'stadium.costs.terrace'): (terrace: Terrace, newSeats: number) => number;
  public get(key: 'stadium.costs.foundation'): (stadium: Stadium) => number;
  public get(key: 'stadium.durations.terrace'): (terrace: Terrace, newSeats: number) => number;
  public get(key: 'stadium.durations.foundation'): (stadium: Stadium) => number;
  public get(key: 'stadium.foundations'): FoundationDefinitionType[];
  public get(key: 'stadium.spectators.terracesFactor'): { [key in TerraceTypeEnum]: number };

  public get(key: 'clubGrounds.totalSlots'): number;
  public get(key: 'clubGrounds.costs.demolish'): (slot: GroundSlot) => number;
  public get(key: 'clubGrounds.costs.unlock'): (slot: GroundSlot) => number;
  public get(key: 'clubGrounds.costs.develop'): (slot: GroundSlot, type: GroundSlotTypeEnum) => number;
  public get(key: 'clubGrounds.durations.demolish'): (slot: GroundSlot) => number;
  public get(key: 'clubGrounds.durations.unlock'): (slot: GroundSlot) => number;
  public get(key: 'clubGrounds.durations.develop'): (slot: GroundSlot, type: GroundSlotTypeEnum) => number;

  public get(key: 'clubGrounds.effects.headquaters.negotiationProgress'): number;
  public get(key: 'clubGrounds.effects.office.additionalStaffMemberSlots'): number;
  public get(key: 'clubGrounds.effects.publicRelations.sponsoringBaseOffer'): number;
  public get(key: 'clubGrounds.effects.scouting.scoutingProgress'): number;
  public get(key: 'clubGrounds.effects.training.trainingProgress'): number;
  public get(key: 'clubGrounds.effects.youth.trainingProgress'): number;
  public get(key: 'clubGrounds.effects.youth.ageLimit'): number;

  public get(key: 'formations.all'): { [key in FormationEnum]: FormationDefinitionType };
  public get(key: 'formations.positions.punishment.wrong'): number;
  public get(key: 'formations.positions.punishment.similar'): number;
  // todo key: PositionEnum not working
  public get(key: 'formations.positions.similar'): { [key: string]: PositionEnum[] };
  public get(key: 'ui.actionScreens.minTimeBetweenActionScreens'): number;
  public get(key: 'ui.actionScreens.animationDuration'): number;
  public get(key: 'ui.notifications.notificationsViewLimit'): number;
  public get(key: 'ui.loadingScreen.waitBefore'): number;
  public get(key: 'ui.loadingScreen.waitAfter'): number;
  public get(key: 'match.evaluation.energyReductionPerTick'): (player: Player) => number;
  public get(key: 'match.evaluation.chanceOwnGoal'): number;
  public get(key: 'match.evaluation.waitWhenDisplayingIncident'): number;
  public get(key: 'match.evaluation.skillBased.totalChances'): (match: Match) => number;
  public get(key: 'match.evaluation.averageYellowCardPerMatch'): number;
  public get(key: 'match.evaluation.averageRedPerMatch'): number;
  public get(key: 'match.evaluation.playerCountLimit'): number;
  public get(key: 'season.waitBetweenDays'): number;

  public get(key: 'teams.minPlayerCount'): (team: Team) => number;
  public get(key: 'teams.percentageForeigners'): (team: Team) => number;

  public get(key: 'players.contract.chanceOfExtendingContract'): (player: Player) => number;
  public get(key: 'players.contract.terminationFee'): (player: Player) => number;
  public get(key: 'players.contract.maxYears'): number;
  public get(key: 'players.contract.endCareer.minAge'): number;
  public get(key: 'players.contract.endCareer.chance'): (player: Player) => boolean;
  public get(key: 'players.contract.contractNegotiation.baseOncePrice'): (player: Player) => number;
  public get(key: 'players.progress.baseMinProgressPerWeek'): number;
  public get(key: 'players.progress.baseMaxProgressPerWeek'): number;
  public get(key: 'players.progress.talentFactor'): number;
  public get(key: 'players.progress.ageSkillReduction'): (player: Player) => number;
  public get(key: 'players.form.baseFormChangeRange'): RangeType;
  public get(key: 'players.form.formChangeRangeFromBaseForm'): RangeType;
  public get(key: 'players.form.formChangeDateRangeInDays'): RangeType;
  public get(key: 'players.form.effectOnSkillFactor'): number;
  public get(key: 'players.energy.dailyEnergyGain'): (player: Player) => number;
  public get(key: 'players.energy.effectOnSkillFactory'): (player: Player) => number;

  public get(key: 'youth.otherTeamRecruitmentLikelihood'): number;
  public get(key: 'youth.scouting.region.progressRangePerAction'): { min: number, max: number, fixed: number };
  public get(key: 'youth.scouting.youthPlayer.progressRangePerAction'): { min: number, max: number, fixed: number };

  public get(key: 'competitions.suspensions.durations'): { [key in CardEnum]: RangeType };
  public get(key: 'competitions.suspensions.yellowLimit'): number;
  public get(key: 'competitions.schedules'): { competition: new (...args: any[]) => Competition, schedule: [number, number][] }[];

  public get(key: 'sponsoring.prestigeFactor'): number;
  public get(key: 'sponsoring.randomPercentRange'): number;
  public get(key: 'sponsoring.types'): {
    [SponsorTypeEnum.SAFE]: SponsorBaseValuesType,
    [SponsorTypeEnum.BALANCED]: SponsorBaseValuesType,
    [SponsorTypeEnum.RISKY]: SponsorBaseValuesType,
  };
  public get(key: 'sponsoring.types.safe.perLeagueMatch'): number;
  public get(key: 'sponsoring.types.safe.perLeagueVictory'): number;
  public get(key: 'sponsoring.types.safe.perCupMatch'): number;
  public get(key: 'sponsoring.types.safe.perCupVictory'): number;
  public get(key: 'sponsoring.types.safe.once'): number;
  public get(key: 'sponsoring.types.balanced.perLeagueMatch'): number;
  public get(key: 'sponsoring.types.balanced.perLeagueVictory'): number;
  public get(key: 'sponsoring.types.balanced.perCupMatch'): number;
  public get(key: 'sponsoring.types.balanced.perCupVictory'): number;
  public get(key: 'sponsoring.types.balanced.once'): number;
  public get(key: 'sponsoring.types.risky.perLeagueMatch'): number;
  public get(key: 'sponsoring.types.risky.perLeagueVictory'): number;
  public get(key: 'sponsoring.types.risky.perCupMatch'): number;
  public get(key: 'sponsoring.types.risky.perCupVictory'): number;
  public get(key: 'sponsoring.types.risky.once'): number;

  public get(key: 'training.talentFactor'): number;
  public get(key: 'training.randomPercentRange'): number;
  public get(key: 'training.actionPointsCosts'): number;
  public get(key: 'training.energyReduction'): (player: Player) => number;

  public get(key: 'staff.actionPointGeneration.negotiating'): (negotiator: Negotiator) => number;
  public get(key: 'staff.actionPointGeneration.scouting'): (scout: Scout) => number;
  public get(key: 'staff.actionPointGeneration.training'): (coach: Coach) => number;
  public get(key: 'staff.actionPointGeneration.trainingAll'): (coach: Coach, targetPositionArea: PositionAreaEnum) => number;
  public get(key: 'staff.terminateContract.costNegotiationActionPoints'): (staffMembers: StaffMember[] | StaffMember) => number;
  public get(key: 'staff.willEndCareer'): (staffMember: StaffMember) => boolean;

  public get(key: 'scouting.actionPointsCosts.region'): number;
  public get(key: 'scouting.actionPointsCosts.youthPlayer'): number;
  public get(key: 'scouting.actionPointsCosts.talent'): number;
  public get(key: 'scouting.actionPointsCosts.newcomers'): number

  public get(key: 'negotiation.actionPointsCosts.newcomers'): number;
  public get(key: 'negotiation.actionPointsCosts.general'): number;

  public get(key: 'currency.coinsExchangeRate'): (type: GateTypeEnum, coins: number) => number;

  public get(key: any): any {
    return get(this.items, key);
  }
}
